import { type ComponentProps, type ReactNode } from "react"
import { flexRender, type Row } from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import type { GroupingRange } from "./table.types"
import { resolveRangeLabel } from "./table.grouping"
import { useTableContext } from "./table.context"

export type TableBodyRowGroupProps<TData = unknown> = ComponentProps<"tr"> & {
  row: Row<TData>
}

/**
 * Checkbox on a group header row that selects/deselects every row under
 * the group. Renders only when the table has a selection column. It
 * toggles the LEAF rows (one state update), never the synthetic group
 * row itself: group rows have no `original`, so letting their ids into
 * the rowSelection state would leak `undefined` rows into consumers
 * like the selection toolbar.
 */
function TableBodyRowGroupCheckbox<TData = unknown>({
  row,
}: {
  row: Row<TData>
}) {
  const { table } = useTableContext<TData>()

  const hasSelectionColumn = table
    .getAllLeafColumns()
    .some((column) => column.id === "__select")
  if (!hasSelectionColumn) return null

  const leafRows = row
    .getLeafRows()
    .filter((leaf) => !leaf.getIsGrouped() && leaf.getCanSelect())
  if (leafRows.length === 0) return null

  const selectedCount = leafRows.filter((leaf) => leaf.getIsSelected()).length
  const allSelected = selectedCount === leafRows.length

  return (
    <input
      type="checkbox"
      className="ui-table-selection-checkbox"
      aria-label="Select group"
      checked={allSelected}
      ref={(el) => {
        if (el) el.indeterminate = selectedCount > 0 && !allSelected
      }}
      // stopPropagation: the group row's own onClick toggles expansion.
      onClick={(e) => {
        e.stopPropagation()
        table.setRowSelection((old) => {
          const next = { ...old }
          for (const leaf of leafRows) {
            if (allSelected) delete next[leaf.id]
            else next[leaf.id] = true
          }
          return next
        })
      }}
      // Read-only since selection is handled in onClick
      onChange={() => {}}
    />
  )
}

function TableBodyRowGroup<TData = unknown>(
  props: TableBodyRowGroupProps<TData>,
) {
  const { row, onClick, className, ...trProps } = props
  const isExpanded = row.getIsExpanded()
  const count = row.subRows.length

  const groupCell = row
    .getVisibleCells()
    .find((cell) => cell.column.id === row.groupingColumnId)

  let groupLabel: ReactNode = String(row.groupingValue ?? "—")

  const groupMeta = groupCell?.column.columnDef.meta as
    | {
        groupingRanges?: GroupingRange[]
        renderGroupLabel?: (value: unknown) => ReactNode
      }
    | undefined
  const ranges = groupMeta?.groupingRanges
  if (ranges) {
    groupLabel = resolveRangeLabel(row.groupingValue as number, ranges)
  } else if (groupMeta?.renderGroupLabel) {
    // Render from the grouping value, not the first row's cell, so mixed
    // groups (e.g. several countries under one flag) are labelled correctly.
    groupLabel = groupMeta.renderGroupLabel(row.groupingValue)
  } else if (groupCell) {
    groupLabel = flexRender(
      groupCell.column.columnDef.cell,
      groupCell.getContext(),
    )
  }

  return (
    <tr
      className={cn(
        "ui-table-body-row-group",
        {
          "ui-table-body-row-group-is-expanded": isExpanded,
        },
        className,
      )}
      onClick={(e) => {
        row.toggleExpanded()
        onClick?.(e)
      }}
      {...trProps}
    >
      <td colSpan={100} className="ui-table-body-row-group-cell">
        <div className="ui-table-body-row-group-header">
          <TableBodyRowGroupCheckbox row={row} />
          <Icon
            className="ui-table-group-expand-icon"
            src={isExpanded ? "angle-small-down" : "angle-small-right"}
          />
          {groupLabel}
          <span className="ui-table-body-row-group-count">({count})</span>
        </div>
      </td>
    </tr>
  )
}

export default TableBodyRowGroup
